import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Sun, CloudSun } from 'lucide-react';
import type { AppointmentPeriodo, SlotsAvailability } from '../_shared/types';

interface Props {
  selectedDate: Date | null;
  onDateSelect: (date: Date) => void;
  selectedPeriodo: AppointmentPeriodo | null;
  onPeriodoSelect: (p: AppointmentPeriodo) => void;
  availability?: SlotsAvailability | null;
  /** Modo encaixe: libera hoje + amanhã e datas com 0 vagas. */
  squeezeMode?: boolean;
}

const MONTH_NAMES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];
const WEEKDAYS = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}
function addDays(d: Date, n: number): Date {
  const x = new Date(d); x.setDate(x.getDate() + n); return x;
}
function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}
function formatDateBR(d: Date): string {
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
}

export default function Calendar({
  selectedDate, onDateSelect, selectedPeriodo, onPeriodoSelect, availability, squeezeMode,
}: Props) {
  const [monthCursor, setMonthCursor] = useState<Date>(() =>
    selectedDate ? new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1) : new Date()
  );

  const today = startOfDay(new Date());
  const tomorrow = addDays(today, 1);
  const dayAfterTomorrow = addDays(today, 2);
  const isAfterBusinessHours = new Date().getHours() >= 16;
  const minBookable = isAfterBusinessHours ? dayAfterTomorrow : tomorrow;

  const grid = useMemo(() => {
    const monthStart = new Date(monthCursor.getFullYear(), monthCursor.getMonth(), 1);
    const monthEnd = new Date(monthCursor.getFullYear(), monthCursor.getMonth() + 1, 0);
    const startWeekday = (monthStart.getDay() + 6) % 7; // segunda = 0
    const cells: Array<Date | null> = [];
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    for (let d = 1; d <= monthEnd.getDate(); d++) {
      cells.push(new Date(monthCursor.getFullYear(), monthCursor.getMonth(), d));
    }
    return cells;
  }, [monthCursor]);

  function vagasFor(date: Date): { manha: number; tarde: number } | null {
    if (!availability) return null;
    const key = formatDateBR(date);
    return availability.vagasPorData[key] ?? availability.limites;
  }

  function isDayBookable(date: Date): boolean {
    const wd = date.getDay();
    if (wd === 0 || wd === 6) return false;
    if (squeezeMode) return date >= today;
    if (date < minBookable) return false;
    const v = vagasFor(date);
    if (v && v.manha <= 0 && v.tarde <= 0) return false;
    return true;
  }

  const periodVagas = selectedDate ? vagasFor(selectedDate) : null;
  const manhaOk = squeezeMode || !periodVagas || periodVagas.manha > 0;
  const tardeOk = squeezeMode || !periodVagas || periodVagas.tarde > 0;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <button
          onClick={() => setMonthCursor(new Date(monthCursor.getFullYear(), monthCursor.getMonth() - 1, 1))}
          className="w-9 h-9 rounded-full bg-hover hover:bg-active flex items-center justify-center"
        >
          <ChevronLeft size={18} className="text-fg-2" />
        </button>
        <div className="font-bold text-fg">
          {MONTH_NAMES[monthCursor.getMonth()]} {monthCursor.getFullYear()}
        </div>
        <button
          onClick={() => setMonthCursor(new Date(monthCursor.getFullYear(), monthCursor.getMonth() + 1, 1))}
          className="w-9 h-9 rounded-full bg-hover hover:bg-active flex items-center justify-center"
        >
          <ChevronRight size={18} className="text-fg-2" />
        </button>
      </div>

      {/* Dias da semana */}
      <div className="grid grid-cols-7 mb-1.5 text-center">
        {WEEKDAYS.map((w) => (
          <div key={w} className="text-[11px] uppercase font-medium text-muted">{w}</div>
        ))}
      </div>

      {/* Grid de dias */}
      <div className="grid grid-cols-7 gap-1">
        {grid.map((d, i) => {
          if (!d) return <div key={`pad-${i}`} className="aspect-square" />;
          const isToday = sameDay(d, today);
          const isSelected = selectedDate && sameDay(d, selectedDate);
          const bookable = isDayBookable(d);

          return (
            <button
              key={d.toISOString()}
              onClick={() => bookable && onDateSelect(d)}
              disabled={!bookable}
              className={`aspect-square rounded-xl text-sm font-medium transition ${
                isSelected ? 'bg-primary-solid text-white'
                  : isToday ? 'bg-active text-fg'
                  : 'text-fg hover:bg-hover'
              } ${!bookable ? 'opacity-25 cursor-not-allowed hover:bg-transparent' : ''}`}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>

      {/* Períodos */}
      {selectedDate && (
        <div className="mt-5">
          <p className="text-[11px] uppercase tracking-wide text-muted mb-2">
            Período em {formatDateBR(selectedDate)}
          </p>
          <div className="grid grid-cols-2 gap-2">
            <PeriodButton
              icon={<Sun size={18} />}
              label="Manhã"
              vagas={periodVagas?.manha}
              active={selectedPeriodo === 'Manhã'}
              disabled={!manhaOk}
              onClick={() => manhaOk && onPeriodoSelect('Manhã')}
            />
            <PeriodButton
              icon={<CloudSun size={18} />}
              label="Tarde"
              vagas={periodVagas?.tarde}
              active={selectedPeriodo === 'Tarde'}
              disabled={!tardeOk}
              onClick={() => tardeOk && onPeriodoSelect('Tarde')}
            />
          </div>
        </div>
      )}

      <p className="text-xs text-muted mt-3 text-center">
        {isAfterBusinessHours
          ? `Após 16h, agendamento só a partir de ${formatDateBR(dayAfterTomorrow)}.`
          : `Próxima data disponível: ${formatDateBR(tomorrow)}.`}
      </p>
    </div>
  );
}

function PeriodButton({
  icon, label, vagas, active, disabled, onClick,
}: {
  icon: React.ReactNode;
  label: string;
  vagas?: number;
  active: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`py-3 rounded-xl border flex flex-col items-center gap-1 transition ${
        disabled ? 'bg-hover/40 border-active/50 opacity-40 cursor-not-allowed'
          : active ? 'bg-primary-solid border-primary text-white'
          : 'bg-hover border-active text-fg hover:border-faint'
      }`}
    >
      {icon}
      <span className="text-sm font-semibold">{label}</span>
      {vagas != null && (
        <span className={`text-[10px] ${active ? 'text-white/80' : 'text-muted'}`}>
          {vagas} vaga{vagas === 1 ? '' : 's'}
        </span>
      )}
    </button>
  );
}
